import { message } from 'antd'
import { store } from '../store'
import { rpc } from '../lib/Rpc'
import { t } from '../config/i18n'
import { runInAction } from 'mobx'
import { TLogger } from '../lib/Logger'
import { AbstractCatalogStore } from '../store/AbstractCatalogStore'
import { TDynamicCatalogConfig } from '../models/DynamicCatalogConfig'
import { TSingleCatalogConfig } from '../models/SingleCatalogConfig'
import { TDynamicCatalogItem } from '../models/DynamicCatalogItem'
import { TSingleCatalogItem } from '../models/SingleCatalogItem'
import { omitFields, uploadFile } from '../lib/utilsCommon'
import { TExtraFieldValue } from '../models/ExtraField'
import { create as createBindingItem, TCatalogBindingItem } from '../models/CatalogBindingItem'
import { PERMISSIONS } from '../models/AbstractCatalogConfig'
import { hasAccess } from '../lib/utilsCatalogs'
import { authService } from './AuthService'
import { fileService } from './FileService'

const CACHE_TTL = 300000
export type TCatalogType = 'dynamic' | 'single'
export type TConfig = TDynamicCatalogConfig | TSingleCatalogConfig
export type TItem = TDynamicCatalogItem | TSingleCatalogItem

export abstract class AbstractCatalogService<C extends TConfig, I extends TItem> {
  public abstract type: TCatalogType
  public abstract store: AbstractCatalogStore<C, I>
  protected abstract logger: TLogger
  protected abstract createConfig: (data: TObjectAny) => C
  protected abstract createItem: (data: TObjectAny) => I
  private runtimeConfigs: C[] = []
  private runtimeConfigsLastLoad = 0
  private lastLoad = 0

  public getConfigItems = async (): Promise<C[]> => {
    if (Date.now() - this.runtimeConfigsLastLoad > CACHE_TTL) {
      const payload = { type: this.type }
      const { error, result } = await rpc.call<any[]>('catalogs.list', payload)
      this.runtimeConfigs = error ? [] : (result || []).map(this.createConfig)
    }
    return this.runtimeConfigs
  }

  public loadConfigItems = async (reload = false) => {
    if (reload || Date.now() - this.lastLoad > CACHE_TTL) {
      store.ui.loading = true
      const payload = { type: this.type }
      const { error, result } = await rpc.call<any[]>('catalogs.list', payload)
      this.store.configItemList = error ? [] : (result || []).map(this.createConfig)
      store.ui.loading = false
      this.lastLoad = Date.now()
    }
  }

  public saveConfigItem = async (item: C): Promise<boolean> => {
    if (!hasAccess(item, PERMISSIONS.CATALOG_UPDATE)) {
      this.logger.error(`saveConfigItem(${item.id}): access denied`)
      return false
    }
    const payload = omitFields(item, ['source'])
    payload.type = this.type
    const { error } = item.id
      ? await rpc.call('catalogs.update', payload)
      : await rpc.call('catalogs.create', payload)
    return !error
  }

  public deleteConfigItem = async (item: C) => {
    if (!hasAccess(item, PERMISSIONS.CATALOG_DELETE)) {
      this.logger.error(`deleteConfigItem(${item.id}): access denied`)
      return
    }
    const payload = { id: item.id, type: this.type }
    await rpc.call('catalogs.delete', payload)
  }

  public editConfigItemById = async (id: string) => {
    this.store.configItemDetails = this.createConfig({})
    await this.loadConfigItems()
    const item = this.store.configItemList.find(i => i.id === id)
    if (item) {
      this.store.configItemDetails = item
    }
  }

  public loadCatalogItems = async (catalogConfigId: string, parentId: undefined | null | number = 0, setPage?: number, setPageSize?: number) => {
    store.ui.loading = true
    if (setPageSize) { this.store.page = setPageSize }
    this.store.page = setPage || 1
    const page = this.store.page
    const pageSize = this.store.pageSize
    const filter = this.store.itemListFilter
    const payload = {
      type: this.type,
      catalogId: catalogConfigId,
      resolveFields: true,
      parentId,
      page,
      pageSize,
      filter,
    }
    const { error, result } = await rpc.call<any>('catalogs.list_items', payload)
    if (result) {
      const config = this.createConfig(result.config)
      const items = (result.items.map(this.createItem) as I[])
        .sort((a, b) => a.order - b.order)
      const path = (result.path || []).map(this.createItem)
      const { page, total } = result
      runInAction(() => {
        this.store.configItemDetails = config
        this.store.itemList = items
        this.store.path = path
        this.store.page = page
        this.store.total = total
      })
    }
    store.ui.loading = false
  }

  public reloadCatalogItems = async () => {
    const catalogConfigId = this.store.configItemDetails.id
    const path = this.store.path
    const parentId = path.length ? path[path.length - 1].id : 0
    return this.loadCatalogItems(catalogConfigId, parentId, 1)
  }

  public setItemListFilter = async (filter: TObjectAny, reload = true) => {
    this.store.itemListFilter = filter
    if (reload) {
      this.reloadCatalogItems().catch(this.logger.error)
    }
  }

  public deleteCatalogItem = async (item: I) => {
    const config = await this.getCatalogConfig(item.catalogId)
    if (!config || !hasAccess(config, PERMISSIONS.ITEM_DELETE)) {
      this.logger.error(`deleteCatalogItem(${item.id}): access denied`)
      return false
    }
    await rpc.call('catalogs.delete_item', { type: this.type, item })
  }

  public editCatalogItemById = async (catalogId: string, itemId: string, parentId: number = 0) => {
    let config = this.store.configItemDetails

    if (config.id !== catalogId) {
      await this.loadCatalogItems(catalogId, parentId)
      config = this.store.configItemDetails
    }

    const item = this.store.itemList.find(i => i._id === itemId) || this.createItem({})
    this.store.itemDetails = this.normalizeItemFields(config, item)
  }

  public saveCatalogItem = async (item: I, restore = false): Promise<boolean> => {
    const config = await this.getCatalogConfig(item.catalogId)
    if (!config || !hasAccess(config, PERMISSIONS.ITEM_UPDATE)) {
      this.logger.error(`saveCatalogItem(${item.id}): access denied`)
      return false
    }
    const payload: TObjectAny = { ...item, type: this.type }
    if (restore) { payload.restore = true }

    const { error } = item._id
      ? await rpc.call('catalogs.update_item', payload)
      : await rpc.call('catalogs.create_item', payload)
    if (!error) {
      this.store.itemDetails = this.createItem({})
    }
    return !error
  }

  public sortItem = async (oldIndex: number, newIndex: number, nextItems: I[]) => {
    const item = this.store.itemList[oldIndex]
    const newOrder = this.store.itemList[newIndex].order ?? 1
    this.store.itemList = nextItems
    this.store.itemLoadingId = item._id

    try {
      await rpc.call('catalogs.update_item_order', {
        type: this.type,
        _id: item._id,
        order: newOrder,
        catalogId: item.catalogId,
      })
    } catch (e) {
      this.logger.error(e)
    } finally {
      this.store.itemLoadingId = null
    }
  }

  public getCatalogConfig = async (id: string): Promise<C | null> => {
    let config = this.store.configItemList.find(i => i.id === id)
    if (!config) {
      const { result, error } = await rpc.call<TObjectAny>('catalogs.get', {
        type: this.type,
        id,
      })
      if (!error && result) {
        config = this.createConfig(result)
      }
    }
    return config ?? null
  }

  public getItemsOptions = async (catalogId: string): Promise<TOption[]> => {
    const payload = { type: this.type, catalogId }
    const { error, result } = await rpc.call<TOption[]>('catalogs.get_items_options', payload)
    return result || []
  }

  public getBindings = async (catalogId: string, itemId: string): Promise<TCatalogBindingItem[]> => {
    const payload = { type: this.type, catalogId, itemId, withData: true }
    const { error, result } = await rpc.call<TObjectAny[]>('catalogs.get_bindings', payload)
    return (result || []).map(createBindingItem)
  }

  public setBindings = async (catalogId: string, itemId: string, bindings: TCatalogBindingItem[]): Promise<boolean> => {
    const config = await this.getCatalogConfig(catalogId)
    if (!config || !hasAccess(config, PERMISSIONS.BINDING)) {
      this.logger.error(`setBindings(${catalogId}): access denied`)
      return false
    }
    const payload = { type: this.type, catalogId, itemId, bindings }
    const { error } = await rpc.call('catalogs.set_bindings', payload)
    return !error
  }

  public exportConfig = async (catalogConfig: C) => {
    const url = authService.createAuthorizedUrl(`/catalogs/export_config`, {
      type: this.type,
      catalogId: catalogConfig.id,
    })
    window.open(url, '_blank')
  }

  public importConfig = async () => {
    const file = await uploadFile('excel')
    store.ui.loading = true
    const { fileToken, error: error1 } = await fileService.uploadTempFile(file)
    if (error1 || !fileToken) {
      store.ui.loading = false
      const errorMessage = `Import: ${error1 || t.common.unknownError}`
      message.error(errorMessage)
      const result = { errors: [errorMessage] }
      console.info(result)
      return result
    }
    const { result, error: error2 } = await rpc.call('catalogs.import_config', {
      type: this.type,
      fileToken,
    })
    if (error2) {
      store.ui.loading = false
      const errorMessage = `Import: ${(error2 as TObjectAny).message || t.common.unknownError}`
      const result = { errors: [errorMessage]}
      console.info(result)
      return result
    }
    const { errors = [] } = result as TObjectAny
    store.ui.loading = false
    fileService.removeTempFile(fileToken).catch(this.logger.error)
    console.info(result)
    this.loadConfigItems(true).catch(this.logger.error)
    if (errors.length) {
      message.warn(t.catalog.importWarning)
    } else {
      message.success(t.catalog.importSuccess)
    }
    return { errors }
  }

  public exportItems = async (catalogConfig: C) => {
    const url = authService.createAuthorizedUrl(`/catalogs/export_items`, {
      type: this.type,
      catalogId: catalogConfig.id,
    })
    window.open(url, '_blank')
  }

  public importItems = async (catalogConfig: C): Promise<{ updated: number, created: number, errors: string[] }> => {
    const file = await uploadFile('excel')
    this.store.itemLoadingId = catalogConfig.id
    const { fileToken, error: error1 } = await fileService.uploadTempFile(file)
    if (error1 || !fileToken) {
      this.store.itemLoadingId = null
      const errorMessage = `Import: ${error1 || t.common.unknownError}`
      message.error(errorMessage)
      const result = { updated: 0, created: 0, errors: [errorMessage] }
      console.info(result)
      return result
    }
    const { result, error: error2 } = await rpc.call('catalogs.import_items', {
      type: this.type,
      catalogId: catalogConfig.id,
      fileToken,
    })
    if (error2) {
      this.store.itemLoadingId = null
      const errorMessage = `Import: ${(error2 as TObjectAny).message || t.common.unknownError}`
      const result = { updated: 0, created: 0, errors: [errorMessage]}
      console.info(result)
      return result
    }
    this.store.itemLoadingId = null
    fileService.removeTempFile(fileToken).catch(this.logger.error)
    message.success(t.catalog.importSuccess)
    const { updated, created, errors } = result as TObjectAny
    console.info(result)
    return { updated, created, errors }
  }


  public exportBindings = async (catalogConfig: C, businessId: string) => {
    const url = authService.createAuthorizedUrl(`/catalogs/export_bindings`, {
      type: this.type,
      catalogId: catalogConfig.id,
      businessId,
    })
    window.open(url, '_blank')
  }

  public importBindings = async (catalogConfig: C, businessId: string): Promise<void> => {
    const file = await uploadFile('excel')
    this.store.itemLoadingId = catalogConfig.id
    const { fileToken, error: error1 } = await fileService.uploadTempFile(file)
    if (error1 || !fileToken) {
      this.store.itemLoadingId = null
      const errorMessage = `Import: ${error1 || t.common.unknownError}`
      message.error(errorMessage)
      return
    }
    const { result, error: error2 } = await rpc.call('catalogs.import_bindings', {
      type: this.type,
      catalogId: catalogConfig.id,
      businessId,
      fileToken,
    })
    if (error2) {
      this.store.itemLoadingId = null
      const errorMessage = `Import: ${error2 || t.common.unknownError}`
      return
    }
    this.store.itemLoadingId = null
    fileService.removeTempFile(fileToken).catch(this.logger.error)
    const { errors = [], successCount } = result as TObjectAny
    if (errors.length) {
      errors.forEach((err: string) => console.warn(err))
      message.warn(t.catalog.importWarning)
    } else {
      message.success(t.catalog.importSuccess)
    }
    console.info({ successCount })
    return
  }

  private normalizeItemFields(config: C, item: I): I {
    const fields: TExtraFieldValue[] = config.fields.map(fieldConfig => {
      return item.fields.find(i => i.fieldID === fieldConfig.id) || {
        fieldID: fieldConfig.id,
        fieldName: fieldConfig.name,
        value: '',
      }
    })
    return { ...item, fields }
  }
}
